import React from 'react';
import { makeObservable, observable } from 'mobx';
import { ThemeStore } from './ThemeStore';
import { SocketStore } from './SocketStore';
import { SessionStore } from './SessionStore';
import { FriendStore } from './FriendStore';
import { UserStore } from './UserStore';
import { BaseStore } from './baseStore';
import { LanguageStore } from './LanguageStore';
import { ChannelStore } from './ChannelStore';
import { E2EEStore } from './E2EEStore';
import { generateKeys } from '../utils/e2ee';

export enum ReadyState {
  LOGOUT = 1 << 0,
  SOCKETID = 1 << 1,
  SESSION = 1 << 2,
  USER_RELATIONS = 1 << 3,
  THEME = 1 << 4,
  BYPASS = 1 << 31,
}

export class Store extends BaseStore<ReadyState> {
  time: number;
  token?: any;
  apiURL: string;
  wsURL: string;
  socket: SocketStore;
  theme: ThemeStore;
  sessions: SessionStore;
  friends: FriendStore;
  user: UserStore;
  language: LanguageStore;
  e2ee: E2EEStore;
  channels: ChannelStore;
  constructor() {
    super(ReadyState.LOGOUT, undefined, 'white');
    this.time = Date.now();
    this.log('Initializing application');
    makeObservable(this, {
      time: observable,
      token: observable,
      apiURL: observable,
      wsURL: observable,
      socket: observable,
      theme: observable,
      sessions: observable,
      friends: observable,
      user: observable,
      language: observable,
      channels: observable,
      e2ee: observable,
    });
    this.token = localStorage.getItem('token') || undefined;
    this.apiURL = 'http://localhost:3000/v1';
    this.wsURL = 'http://localhost:3001';
    this.user = new UserStore(this);
    this.socket = new SocketStore(this);
    this.theme = new ThemeStore(this);
    this.friends = new FriendStore(this);
    this.sessions = new SessionStore(this);
    this.language = new LanguageStore(this);
    this.channels = new ChannelStore(this);
    this.e2ee = new E2EEStore(this);

    (async () => {
      const keys = await generateKeys();
      console.log("identityKey:", atob(keys.identityKey).length); // 32–33
      console.log("preKey:", atob(keys.preKey).length);           // 32–33
      console.log("signedPreKey:", atob(keys.signedPreKey).length); // 32–33
      console.log("signature:", atob(keys.signature).length);       // 64 ✅
    })
    if (this.token) {
      this.user.loadUserData().then(() => {
        this.e2ee.init();
        this.friends.loadFriends();
      })
    }
    this.log('Initializing application is finished in ' + (Date.now() - this.time) + ' ms');
  }

  logout() {
    this.setToken(null);
    this.socket.logout();
    this.sessions.logout();
    this.friends.logout();
    this.user.logout();
    this.theme.logout();
    this.language.logout();
    this.channels.logout();
    this.e2ee.logout();
    this.setState(ReadyState.LOGOUT);
  }

  isReady() {
    return (
      this.socket.isReady() &&
      this.theme.isReady() &&
      this.sessions.isReady() &&
      this.friends.isReady() &&
      this.user.isReady() &&
      this.language.isReady() &&
      this.channels.isReady()
    );
  }

  logStoreState() {
    const stores = {
      socket: this.socket,
      theme: this.theme,
      sessions: this.sessions,
      friends: this.friends,
      user: this.user,
      language: this.language,
      channels: this.channels,
    };

    const notReady = Object.entries(stores)
      .filter(([_, store]) => !store.isReady())
      .map(([key]) => key);

    if (notReady.length > 0) {
      this.log("Stores not ready:", JSON.stringify(notReady, null, 2));
    } else {
      this.log("✅ All stores ready");
    }
  }

  reload() {
    this.socket.reload();
    this.theme.reload();
    this.sessions.reload();
    this.friends.reload();
    this.user.reload();
    this.language.reload();
    this.channels.reload();
    this.e2ee.reload();
  }

  setToken(token: string | null) {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    this.token = token;

    if (token) {
      this.friends.loadFriends();
    }
  }
}
const StoreContext = React.createContext<Store>({} as Store);
export const store = new Store();

export const StoreProvider = ({ children }: { children: React.ReactElement }) => {
  return <StoreContext.Provider value={store}> {children} </StoreContext.Provider>;
};

export const useStore = () => React.useContext(StoreContext);
